import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/hooks/useTenant";
import { fetchAllCollectionRows, fetchCollectionRows, isMissingCollectionsTable } from "@/lib/collectionsApi";
import { resolveCollections, type Collection, type CollectionRow } from "@/lib/collections";

export const collectionsQueryKey = (tenantId: string) => ["collections", tenantId] as const;
export const adminCollectionsQueryKey = ["collections-admin"] as const;

/**
 * The published collections this site shows (its own rows over the global
 * ones). Fetched once per session; a database without the table yet yields
 * an empty list rather than an error, so no page depends on the SQL having
 * been pasted.
 */
export function useCollections() {
  const tenant = useTenant();
  const query = useQuery({
    queryKey: collectionsQueryKey(tenant.id),
    queryFn: async (): Promise<CollectionRow[]> => {
      try {
        return await fetchCollectionRows(tenant.id);
      } catch (e) {
        console.warn("collections unavailable", e);
        return [];
      }
    },
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const collections = useMemo(() => resolveCollections(query.data ?? [], tenant.id), [query.data, tenant.id]);
  const bySlug = useMemo(() => new Map(collections.map((c) => [c.slug, c])), [collections]);
  return { collections, bySlug, isLoading: query.isLoading };
}

export function useCollection(slug: string | undefined): { collection: Collection | null; isLoading: boolean } {
  const { bySlug, isLoading } = useCollections();
  return { collection: slug ? bySlug.get(slug) ?? null : null, isLoading };
}

/** Every row of every site, published or not, the admin panel's list. */
export function useAdminCollections() {
  const query = useQuery({
    queryKey: adminCollectionsQueryKey,
    queryFn: fetchAllCollectionRows,
    staleTime: 30_000,
    retry: false,
  });
  return {
    rows: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    missingTable: !!query.error && isMissingCollectionsTable(query.error),
  };
}

export function useInvalidateCollections() {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["collections"] }),
      qc.invalidateQueries({ queryKey: adminCollectionsQueryKey }),
    ]);
  };
}
